import type { App } from 'vue'
import VueAMap, { initAMapApiLoader, lazyAMapApiLoaderInstance } from '@vuemap/vue-amap'
import VueAMapLoca from '@vuemap/vue-amap-loca'
import '@vuemap/vue-amap/dist/style.css'
import { apiConfig } from '@/config/api'

// 高德 SDK 的按需加载。
//
// 懒的是**脚本**：webapi.amap.com/maps，283KB gzip / 945KB 未压缩。
// 原本 main.ts 启动时就调 initAMapApiLoader，哪怕当前页面根本不显示地图。
// 首页改版后地图收进弹窗，这笔开销更没有理由留在首屏。
//
// ⚠️ 组件注册（app.use）必须留在启动时，不能一起懒。
//
// 试过把 @vuemap/vue-amap 也改成动态 import、在 ensureAMapLoaded 里才 app.use，
// 想把它那几个 chunk（约 250KB 未压缩）也挪出首屏。dev 下看着正常，生产构建直接
// 坏掉：大师地图没有图钉、路线展示没有折线和无人机。
//
// 原因是 el-amap-* 全靠**全局注册**解析（vite.config 的 AutoImport 名单里只有
// ElAmapMarker/ElAmapPolyline/ElAmap 三个，而且对模板里的 kebab-case 写法没生效，
// el-amap-marker-cluster 压根不在名单里）。父组件渲染到 <el-amap-marker> 时会调
// resolveComponent，那一刻 ensureAMapLoaded 的 await 还没回来 —— 组件没注册，
// Vue 就把它当未知元素原样渲染。产物里能直接看到 15 个 .route-panel-point
// 裸在普通 DOM 里、一个 .amap-marker 都没有。
//
// 而且这种坏法是**静默**的：不报错、不白屏，只是图钉和折线消失。
// 以后谁新写一个 <el-amap-xxx> 都会踩到，所以不留这个雷 —— 注册放回启动时。
// 真正的大头（那 283KB gzip 的 SDK 脚本）仍然是懒的，优化的主要收益还在。
//
// ⚠️ 另外不要直接 await lazyAMapApiLoaderInstance。它在 loader 没初始化前是 null，
// `await null` 会立刻 resolve，然后调用方碰到 undefined 的 AMap 直接炸。
// 一律走 ensureAMapLoaded()。

let task: Promise<void> | null = null

export const ensureAMapLoaded = () => {
  if (task) return task
  const { AMAP_KEY, AMAP_SECURITY_JS_CODE } = apiConfig
  initAMapApiLoader({
    key: AMAP_KEY,
    securityJsCode: AMAP_SECURITY_JS_CODE, // 新版key需要配合安全密钥使用
    version: '2.0',
    // 插件与 Loca 都不在这里加载，见 amap-plugin.ts / amap-loca.ts
    plugins: [],
  })
  // initAMapApiLoader 是同步给 lazyAMapApiLoaderInstance 赋值的，
  // 所以这一行读到的一定是刚建好的 Promise。
  //
  // 失败要把缓存清掉：task 缓存的是 Promise 本身，连 rejected 的一起缓存，
  // 不清的话「重试」按钮拿回来的还是同一个已经失败的 Promise，永远好不了。
  // 弱网下第一次加载失败是常事，必须留出重试的路。
  task = Promise.resolve(lazyAMapApiLoaderInstance)
    .then(() => undefined)
    .catch((error) => {
      task = null
      throw error
    })
  return task
}

/** 只注册组件，不加载 SDK。SDK 由 ensureAMapLoaded 按需拉。 */
export const registerAMapComponents = (app: App) => {
  app.use(VueAMap)
  app.use(VueAMapLoca)
}
